/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Grid } from '@mui/material';
import React, { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import {
  ReactHookFormHiddenInput,
  isGeometryDirty,
} from '@opengeoweb/form-fields';
import { usePrevious } from '@opengeoweb/shared';
import type { CoreAppStore } from '@opengeoweb/store';
import {
  uiSelectors,
  genericSelectors,
  mapSelectors,
  uiTypes,
} from '@opengeoweb/store';
import type { OpenLayersLayerProps } from '@opengeoweb/webmap-react';
import {
  addFeatureProperties,
  rewindGeometry,
  useMapDrawTool,
} from '@opengeoweb/webmap-react';
import { useTACApi } from '../../../api/hooks';
import type {
  Airmet,
  AviationPhenomenon,
  AviationProduct,
  CancelAirmet,
  FormMode,
  ProductConfig,
} from '../../../types';
import { StartOrEndDrawing } from '../../../types';
import { airmetConfig as defaultAirmetConfig } from '../../../utils/config';
import { useSigmetAirmetTranslation } from '../../../utils/i18n';
import {
  featurePropsFIR,
  featurePropsIntersectionStart,
  featurePropsStart,
} from '../../MapViewGeoJson/constants';
import { formStyles } from '../ProductForm.styles';
import {
  Change,
  CloudLevels,
  Levels,
  ObservationForecast,
  ObservationForecastTime,
  Phenomenon,
  Progress,
  SelectFIR,
  StartGeometry,
  SurfaceVisibility,
  SurfaceWind,
  Type,
  ValidFrom,
  ValidUntil,
} from '../ProductFormFields';
import IssuedAt from '../ProductFormFields/IssuedAt';
import Tac from '../ProductFormFields/Tac';
import ProductFormLayout from '../ProductFormLayout';
import { useTAC } from '../ProductFormTac';
import { useGeoJSONHandling } from '../useStoredGeoJSONHandling';
import {
  getDefaultPhenomena,
  getDrawTools,
  getFir,
  getProductIssueDate,
  triggerValidations,
  updateDrawToolsWithFir,
  useFormLayers,
} from '../utils';

const useConditionalFields = (): {
  hasSurfaceVisiblity: boolean;
  hasSurfaceWind: boolean;
  hasLevels: boolean;
  hasCloudLevels: boolean;
} => {
  const { watch, unregister } = useFormContext();
  // phenomenon: SFC_VIS
  const phenomenon = watch('phenomenon');
  const hasSurfaceVisiblity = phenomenon === 'SFC_VIS';
  React.useEffect(() => {
    if (!hasSurfaceVisiblity) {
      unregister(['visibilityValue', 'visibilityCause']);
    }
  }, [hasSurfaceVisiblity, unregister]);

  // phenomenon: SFC_WIND
  const hasSurfaceWind = watch('phenomenon') === 'SFC_WIND';
  React.useEffect(() => {
    if (!hasSurfaceWind) {
      unregister(['windDirection', 'windUnit', 'windSpeed']);
    }
  }, [hasSurfaceWind, unregister]);

  // phenomenon: SFC_WIND, SFC_VIS, BKN_CLD, OVC_CLD
  const hasLevels =
    phenomenon !== 'SFC_WIND' &&
    phenomenon !== 'SFC_VIS' &&
    phenomenon !== 'BKN_CLD' &&
    phenomenon !== 'OVC_CLD';

  React.useEffect(() => {
    if (!hasLevels) {
      unregister(['levelInfoMode', 'level', 'lowerLevel']);
    }
  }, [hasLevels, unregister]);

  // phenomenon: BKN_CLD
  const hasCloudLevels = phenomenon === 'BKN_CLD' || phenomenon === 'OVC_CLD';
  React.useEffect(() => {
    if (!hasCloudLevels) {
      unregister(['cloudLevelInfoMode', 'cloudLevel', 'cloudLowerLevel']);
    }
  }, [hasCloudLevels, unregister]);

  return { hasSurfaceVisiblity, hasSurfaceWind, hasLevels, hasCloudLevels };
};

export interface AirmetFormProps {
  mode: FormMode;
  isCancelAirmet?: boolean;
  initialAirmet?: Airmet;
  initialCancelAirmet?: CancelAirmet;
  showMap?: boolean;
  productConfig?: ProductConfig;
  product?: AviationProduct;
  getTAC?: (product: AviationProduct) => Promise<{ data: string }>;
  storedGeoJSON?: GeoJSON.FeatureCollection;
  lockValidFrom?: boolean;
}

const AirmetForm: React.FC<AirmetFormProps> = ({
  mode,
  isCancelAirmet = false,
  initialAirmet = null!,
  initialCancelAirmet = null!,
  showMap = true,
  productConfig = defaultAirmetConfig,
  product,
  getTAC,
  storedGeoJSON,
  lockValidFrom,
}: AirmetFormProps) => {
  const {
    watch,
    setValue,
    handleSubmit,
    getValues,
    trigger,
    formState: { errors },
  } = useFormContext();
  const [phenomenaList, setPhenomenaList] = React.useState<
    AviationPhenomenon[]
  >(getDefaultPhenomena(productConfig).phenomena);
  const [hasUserChangedTime, setHasUserChangedTime] = React.useState(false);

  const { t } = useSigmetAirmetTranslation();
  const { fetchAirmetTAC } = useTACApi();
  const firBorderAtStart = initialAirmet
    ? initialAirmet.locationIndicatorATSR
    : productConfig.active_firs[0];

  const sourcePanelId = useSelector((store: CoreAppStore) =>
    uiSelectors.getSourcePanelId(store, uiTypes.DialogTypes.PublicWarnings),
  );

  const linkedMaps: string[] = useSelector((store) =>
    genericSelectors.getLinkedMapIds(store, sourcePanelId!),
  );

  const getSrsForMapId = useSelector((store: CoreAppStore) =>
    linkedMaps ? mapSelectors.getSrs(store, linkedMaps[0]) : undefined,
  );

  // Fallback to Mercator for '', null, or undefined
  const projection = getSrsForMapId?.trim() ? getSrsForMapId : 'EPSG:3857';

  const prevProjection = usePrevious(projection);

  // Trigger update if projection changes
  useEffect(() => {
    if (prevProjection === undefined) {
      return;
    }
    if (projection !== prevProjection) {
      if (startGeoJSON?.features?.length) {
        updateStartGeoJson(startGeoJSON);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projection, prevProjection]);

  const {
    geoJSON: startGeoJSON,
    setGeoJSON,
    geoJSONIntersection: startGeoJSONIntersection,
    getLayer: getStartLayer,
    drawModes: startEditModes,
    activeTool: activeStartTool,
    changeActiveTool: changeActiveStartTool,
    deactivateTool: deactivateStartTool,
    setGeoJSONIntersectionBounds: setStartGeoJSONIntersectionBounds,
    setDrawModes: setStartEditModes,
  } = useMapDrawTool({
    defaultDrawModes: getDrawTools(
      t,
      StartOrEndDrawing.start,
      getFir(productConfig, firBorderAtStart),
    ),
    defaultGeoJSON:
      !isCancelAirmet && initialAirmet?.startGeometry
        ? addFeatureProperties(initialAirmet.startGeometry, featurePropsStart)
        : undefined,
    defaultGeoJSONIntersection:
      !isCancelAirmet && !!initialAirmet?.startGeometry
        ? addFeatureProperties(
            initialAirmet.startGeometryIntersect,
            featurePropsIntersectionStart,
          )
        : undefined,
    defaultGeoJSONIntersectionBounds: addFeatureProperties(
      getFir(productConfig, firBorderAtStart),
      featurePropsFIR,
    ),
    projection,
  });

  const getHasFormErrors = (): boolean => Object.keys(errors).length > 0;

  const [tac, updateTac] = useTAC(
    t,
    initialAirmet ?? initialCancelAirmet,
    (getTAC ?? fetchAirmetTAC) as (
      product: AviationProduct,
    ) => Promise<{ data: string }>,
    getHasFormErrors,
  );

  React.useEffect(() => {
    updateTac(watch);
  }, [initialAirmet, updateTac, watch]);

  const onChangeForm = useCallback((): void => {
    updateTac(watch);
  }, [updateTac, watch]);

  const onUpdateStartgeoJSON = useCallback(
    (
      updatedStartGeoJSON: GeoJSON.FeatureCollection,
      updateStartGeoJSONIntersection: GeoJSON.FeatureCollection,
    ): void => {
      setValue('startGeometry', updatedStartGeoJSON, {
        shouldValidate: false,
        shouldDirty: isGeometryDirty(
          watch('startGeometry'),
          updatedStartGeoJSON,
        ),
      });
      setValue('startGeometryIntersect', updateStartGeoJSONIntersection, {
        shouldValidate: false,
        shouldDirty: isGeometryDirty(
          watch('startGeometryIntersect'),
          updateStartGeoJSONIntersection,
        ),
      });
      queueMicrotask(() => void trigger('startGeometry'));

      onChangeForm();
    },
    [setValue, watch, onChangeForm, trigger],
  );

  const updateStartGeoJson = useCallback(
    (newGeoJSON?: GeoJSON.FeatureCollection): void => {
      const [newStartGeoJson, newStartIntersection] = setGeoJSON(newGeoJSON!);
      const newGeoStart = rewindGeometry(newStartGeoJson);
      const newGeoStartIntersect = rewindGeometry(newStartIntersection!);
      onUpdateStartgeoJSON(newGeoStart, newGeoStartIntersect!);
    },
    [setGeoJSON, onUpdateStartgeoJSON],
  );

  const { onChangeStartTool, drawToolsDisabled } = useGeoJSONHandling({
    storedGeoJSON,
    startGeoJSON,
    setGeoJSON,
    updateStartGeoJson,
    deactivateStartTool,
    changeActiveStartTool,
    startEditModes,
    currentStartDrawTool: activeStartTool,
  });

  const layers: OpenLayersLayerProps[] = [
    getStartLayer('geoJSONIntersectionBounds', 'geojsonlayer-fir'),
    {
      ...getStartLayer('geoJSON', 'geojsonlayer-start'),
      exitDrawModeCallback: deactivateStartTool,
      updateGeojson: updateStartGeoJson,
    },
    getStartLayer('geoJSONIntersection', 'geojsonlayer-intersection-start'),
  ];

  useFormLayers(layers);

  const [isDisabled, setIsDisabled] = React.useState(mode === 'view');
  const isReadOnly = mode === 'view';
  const helperText = isReadOnly ? '' : 'Optional';

  React.useEffect(() => {
    const isInDrawMode = activeStartTool !== '';
    if (mode === 'view') {
      setIsDisabled(true);
    } else {
      setIsDisabled(isInDrawMode);
    }
  }, [activeStartTool, mode]);

  const { hasSurfaceVisiblity, hasSurfaceWind, hasLevels, hasCloudLevels } =
    useConditionalFields();

  const onChangeFir = (
    newPhenomenaList: AviationPhenomenon[],
    event?: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const newFIRValue = event?.target.value;
    const newFirGeoJSON = addFeatureProperties(
      getFir(productConfig, newFIRValue),
      featurePropsFIR,
    );
    // delete shape
    onChangeStartTool(startEditModes.find((mode) => mode.value === 'DELETE')!);
    // update instersection bounds
    setStartGeoJSONIntersectionBounds(newFirGeoJSON);
    // update tool buttons with new fir geoJSON
    setStartEditModes(updateDrawToolsWithFir(startEditModes, newFirGeoJSON));
    // update available phenomena for the FIR
    setPhenomenaList(newPhenomenaList);

    // trigger validation for airmet specific fields that depend on FIR config
    const fieldnames = [
      'windDirection',
      'windSpeed',
      'visibilityValue',
      'cloudLevel.value',
      'cloudLowerLevel.value',
    ];
    triggerValidations(fieldnames, getValues, trigger);
  };

  const renderForm = (): React.ReactElement => (
    <Grid container direction="column" spacing={2} sx={formStyles}>
      {/* Type */}
      <Type
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        onChange={onChangeForm}
        productType="airmet"
      />
      {/* Phenomenon */}
      {!isCancelAirmet && (
        <Phenomenon
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          onChange={onChangeForm}
          phenomenaList={phenomenaList}
          productConfig={productConfig}
          productType="airmet"
        />
      )}
      {/* Surface Visibility */}
      {!isCancelAirmet && hasSurfaceVisiblity ? (
        <SurfaceVisibility
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          onChange={onChangeForm}
          productConfig={productConfig}
        />
      ) : null}
      {/* Surface Wind */}
      {!isCancelAirmet && hasSurfaceWind ? (
        <SurfaceWind
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          onChange={onChangeForm}
          productConfig={productConfig}
        />
      ) : null}
      {/* Observed/Forecast */}
      {!isCancelAirmet && (
        <ObservationForecast isDisabled={isDisabled} isReadOnly={isReadOnly} />
      )}
      {/* At */}
      {!isCancelAirmet && (
        <ObservationForecastTime
          helperText={helperText}
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          onChange={onChangeForm}
        />
      )}
      {/* Valid from */}
      <ValidFrom
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        lockBecauseActive={lockValidFrom}
        onChange={onChangeForm}
        onUserChange={() => setHasUserChangedTime(true)}
        productConfig={productConfig}
      />
      {/* Valid until */}
      <ValidUntil
        hasUserChangedTime={hasUserChangedTime}
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        onChange={onChangeForm}
        onUserChange={() => setHasUserChangedTime(true)}
        productConfig={productConfig}
      />
      {/* Where */}
      <SelectFIR
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        onChange={(event, newPhenomenaList) => {
          onChangeFir(newPhenomenaList ?? [], event);
        }}
        productConfig={productConfig}
      />
      {/* Draw */}
      <StartGeometry
        activeTool={activeStartTool}
        geoJSON={startGeoJSON}
        geoJSONIntersection={startGeoJSONIntersection}
        isDisabled={drawToolsDisabled}
        isReadOnly={isReadOnly}
        onChangeTool={onChangeStartTool}
        productConfig={productConfig}
        tools={startEditModes}
      />
      {/* Levels */}
      {!isCancelAirmet && hasLevels ? (
        <Levels
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          onChange={onChangeForm}
          productConfig={productConfig}
          productType="airmet"
        />
      ) : null}
      {/* Cloud Levels */}
      {!isCancelAirmet && hasCloudLevels ? (
        <CloudLevels
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          onChange={onChangeForm}
          productConfig={productConfig}
        />
      ) : null}
      {/* Progress */}
      {!isCancelAirmet && (
        <Progress
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
          onChange={onChangeForm}
          productConfig={productConfig}
          productType="airmet"
        />
      )}
      {/* Change */}
      {!isCancelAirmet && (
        <Change isDisabled={isDisabled} isReadOnly={isReadOnly} />
      )}
      {/* Issued at */}
      <IssuedAt
        date={getProductIssueDate(t, initialAirmet, initialCancelAirmet)}
        isReadOnly={isReadOnly}
      />

      <Tac product={product!} tac={tac}>
        {/* Hidden sequence number field needed for the TAC generation */}
        <ReactHookFormHiddenInput defaultValue="-1" name="sequence" />
        {
          /* Add hidden fields for TAC generation in case of cancel airmet */
          isCancelAirmet ? (
            <>
              <ReactHookFormHiddenInput name="cancelsAirmetSequenceId" />
              <ReactHookFormHiddenInput name="validDateEndOfAirmetToCancel" />
              <ReactHookFormHiddenInput name="validDateStartOfAirmetToCancel" />
            </>
          ) : null
        }
      </Tac>
    </Grid>
  );

  if (!showMap) {
    return renderForm();
  }

  return (
    <form
      onChange={onChangeForm}
      onSubmit={handleSubmit(() => null)}
      style={{ width: '100%' }}
    >
      <ProductFormLayout geoJSONLayers={layers} productConfig={productConfig}>
        {renderForm()}
      </ProductFormLayout>
    </form>
  );
};

export default AirmetForm;
